import { apiFetch } from './api';
import type { IsoDate } from './dates';

/**
 * The employee app's client.
 *
 * Mirrors apps/api/src/routes/employee.ts. Every call is implicitly "me" — no
 * function here takes a staff id, because the API does not accept one. That is
 * the whole security model of this surface, so keep it that way: a `staffId`
 * parameter appearing in this file is a bug, not a feature.
 */

export interface EmployeeProfile {
    readonly staffId: number;
    readonly staffName: string;
    readonly staffTitleId: number | null;
    readonly staffTitle: string | null;
    readonly canTradeSchool: boolean;
    readonly deductsLunch: boolean;
    readonly canPayNotes: boolean;
    readonly showAllOnCalendar: boolean;
    readonly showTimesheetToggle: boolean;
}

export interface OpenShift {
    readonly id: number;
    readonly jobId: number | null;
    readonly clientId: number | null;
    readonly clientName: string | null;
    readonly jobNumber: number | null;
    readonly siteAddress: string | null;
    readonly jobOther: string | null;
    readonly workDate: IsoDate;
    readonly startTime: string | null;
    readonly ratePerHour: number | null;
    readonly isTradeSchool: boolean;
}

export interface PickerClient {
    readonly id: number;
    readonly name: string;
}

export interface PickerJob {
    readonly id: number;
    readonly clientId: number | null;
    readonly jobNumber: number;
    readonly address: string;
}

/** One of this person's own bookings for today — what a Start button acts on. */
export interface MyBooking {
    readonly eventId: number;
    readonly jobId: number | null;
    readonly jobNumber: string | null;
    readonly clientName: string | null;
    readonly siteAddress: string | null;
    readonly categoryName: string | null;
    readonly startTime: string | null;
    readonly endTime: string | null;
}

export interface EmployeeHome {
    readonly today: IsoDate;
    readonly profile: EmployeeProfile;
    readonly openShift: OpenShift | null;
    /** A shift left running from an earlier day, closed at standard hours as the screen loaded. */
    readonly closedForgotten: ForgottenShift | null;
    readonly todayBookings: readonly MyBooking[];
    readonly clients: readonly PickerClient[];
    readonly jobs: readonly PickerJob[];
    readonly hoursToday: number;
}

export interface SwmsStatus {
    readonly required: boolean;
    readonly jobId: number | null;
    readonly reason: 'new' | 'expired' | null;
    readonly lastSignedAt: string | null;
}

/** A shift nobody clocked off, closed automatically at standard hours. */
export interface ForgottenShift {
    readonly entryId: number;
    readonly workDate: IsoDate;
    readonly startTime: string;
    readonly finishDate: IsoDate;
    readonly finishTime: string;
    readonly hours: number;
}

export interface StartResult {
    readonly shift: OpenShift;
    readonly swms: SwmsStatus;
    readonly closedForgotten: ForgottenShift | null;
}

export interface StopResult {
    readonly entryId: number;
    readonly totalHours: number;
    readonly overtime15: number;
    readonly overtime2: number;
    readonly lunchDeducted: boolean;
    readonly costLabour: number | null;
    readonly isTradeSchool: boolean;
    readonly autoClosed: ForgottenShift | null;
}

export interface DayCrewMember {
    readonly staffId: number;
    readonly name: string;
    readonly title: string | null;
    readonly colour: string | null;
    readonly textColour: string | null;
    readonly isMe: boolean;
}

export interface DayNote {
    readonly id: number;
    readonly body: string;
}

export interface DayBooking {
    readonly eventId: number;
    readonly jobId: number | null;
    readonly jobNumber: string | null;
    readonly clientName: string | null;
    readonly siteAddress: string | null;
    readonly categoryName: string | null;
    readonly startTime: string | null;
    readonly endTime: string | null;
    readonly crew: readonly DayCrewMember[];
    readonly myHours: number | null;
    readonly notes: readonly DayNote[];
}

export interface MyDay {
    readonly date: IsoDate;
    readonly showingAll: boolean;
    readonly bookings: readonly DayBooking[];
    readonly generalNotes: readonly DayNote[];
    readonly hoursToday: number;
}

export type RequestableLeave = 'annual_leave' | 'sick_leave' | 'compassionate_leave';

export const LEAVE_LABELS: Readonly<Record<string, string>> = {
    annual_leave: 'Annual Leave',
    sick_leave: 'Sick Leave',
    compassionate_leave: 'Compassionate Leave',
};

export interface MyLeaveRequest {
    readonly id: number;
    readonly leaveType: string;
    readonly startsOn: IsoDate;
    readonly endsOn: IsoDate;
    readonly status: string;
    readonly requestedAt: string;
    readonly decidedAt: string | null;
    readonly decidedByName: string | null;
}

export interface MyPayNote {
    readonly id: number;
    readonly noteDate: IsoDate;
    readonly note: string;
    readonly createdAt: string;
}

export interface TradeSchoolResult {
    readonly eventId: number;
    readonly datesAdded: number;
    readonly weekendsSkipped: number;
    readonly alreadyBooked: number;
}

export interface SwmsTaskCard {
    readonly id: number;
    readonly task: string;
    readonly hazardsRisks: string | null;
    readonly controlMeasures: string | null;
    readonly riskLevel: number | null;
}

export interface SwmsForJob {
    readonly jobId: number;
    readonly jobNumber: number;
    readonly address: string;
    readonly clientName: string | null;
    readonly status: SwmsStatus;
    readonly tasks: readonly SwmsTaskCard[];
}

// ─── Calls ──────────────────────────────────────────────────────────────────

function post<T>(path: string, body: unknown): Promise<T> {
    return apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function fetchHome(): Promise<EmployeeHome> {
    return apiFetch<EmployeeHome>('/me/home');
}

export function startShift(input: { jobId: number } | { tradeSchool: true }): Promise<StartResult> {
    return post<StartResult>('/me/shifts/start', input);
}

export function stopShift(note: string | null): Promise<StopResult> {
    return post<StopResult>('/me/shifts/stop', { note });
}

export function fetchDay(date: IsoDate): Promise<MyDay> {
    return apiFetch<MyDay>(`/me/day?date=${encodeURIComponent(date)}`);
}

export function fetchMonth(from: IsoDate, to: IsoDate): Promise<readonly IsoDate[]> {
    return apiFetch<{ dates: IsoDate[] }>(
        `/me/month?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ).then((r) => r.dates);
}

export function fetchLeaveRequests(): Promise<readonly MyLeaveRequest[]> {
    return apiFetch<{ requests: MyLeaveRequest[] }>('/me/leave-requests').then((r) => r.requests);
}

export function requestLeave(input: {
    leaveType: RequestableLeave;
    startsOn: IsoDate;
    endsOn: IsoDate;
}): Promise<MyLeaveRequest> {
    return post<MyLeaveRequest>('/me/leave-requests', input);
}

export function fetchPayNotes(): Promise<readonly MyPayNote[]> {
    return apiFetch<{ notes: MyPayNote[] }>('/me/pay-notes').then((r) => r.notes);
}

export function addPayNote(input: { noteDate: IsoDate; note: string }): Promise<MyPayNote> {
    return post<MyPayNote>('/me/pay-notes', input);
}

export function addTradeSchool(from: IsoDate, to: IsoDate): Promise<TradeSchoolResult> {
    return post<TradeSchoolResult>('/me/trade-school', { from, to });
}

export function fetchSwms(jobId: number): Promise<SwmsForJob> {
    return apiFetch<SwmsForJob>(`/me/swms/${String(jobId)}`);
}

export function signSwms(
    jobId: number,
    input: { personResponsible: string; signature: string },
): Promise<{ id: number; jobId: number; signedAt: string }> {
    return post(`/me/swms/${String(jobId)}`, input);
}
